import { supabase } from '../../../lib/supabaseClient';
import { structuredLogger } from '../../../lib/structuredLogger';
import { logTaskTransition } from '../../../services/capacidadeService';
import { sendWorkflowCompletedEmail } from '../../../services/emailService';
import { notifyTaskAssigned, notifyTaskTransferred, notifyEscalation, notifyTaskReturned } from '../../../services/notificationProducer';
import { getUserAssignments, type UserAssignment } from '../../../services/organizationStructureService';
import { applyScopeFilter, type DataScope } from '../../../services/scopeService';
import type {
  TaskListItem,
  TaskListResult,
  TaskDetailItem,
  TaskFilters,
  TaskSortField,
  TaskCompletionPayload,
  TaskClaimPayload,
  TaskReassignPayload,
  TaskSkipPayload,
  TaskSLAAdjustPayload,
  TaskMetadata,
  TaskOperationalHistoryEntry,
  TaskOperationalMetrics,
} from '../types/task';
import type { TaskResult } from '../../../types/workflow-v9';
import { parseTaskDetail } from '../types/task';
import type { SortDirection } from '../types/task';
import {
  listUserWorkflowRoles,
  listUserRoleIds,
  normalizeWorkflowRoleKey,
  userHasWorkflowRole,
} from './workflowRoleService';

// ============================================================================
// MAPEAMENTO DB → TIPO
// ============================================================================

function mapRowToTaskListItem(row: Record<string, any>): TaskListItem {
  const metadata = (row.metadata ?? {}) as TaskMetadata;
  const stageRole = row.stage?.default_assignee_role ?? metadata.role ?? null;
  const assignedRoleKey = metadata.role ? normalizeWorkflowRoleKey(String(metadata.role)) : normalizeWorkflowRoleKey(stageRole);
  const assignedRoleName = metadata.roleName ?? metadata.role ?? stageRole ?? null;
  const assignedUserId = row.assigned_to ?? null;
  const assignedUserName = row.assigned_to_name ?? null;
  const processInstance = row.processInstance ?? row.workflow_instances_v9 ?? null;

  return {
    id: row.id,
    instanceId: row.instance_id,
    instanceTitle: row.instance_title ?? processInstance?.title ?? null,
    referenceCode: row.reference_code ?? processInstance?.reference_code ?? null,
    stageId: row.stage_id,
    stageName: row.stage_name ?? row.stage?.name ?? null,
    status: row.status,
    assignedRoleId: metadata.roleId ?? null,
    assignedRoleKey,
    assignedRoleName,
    assignedUserId,
    assignedUserName,
    assignedTo: row.assigned_to ?? null,
    assignedToName: row.assigned_to_name ?? null,
    claimedAt: row.assigned_at ?? null,
    isRoleQueue: Boolean(assignedRoleKey && !assignedUserId),
    priority: row.priority ?? processInstance?.priority ?? null,
    startedAt: row.started_at ?? null,
    completedAt: row.completed_at ?? null,
    slaDeadline: row.sla_deadline ?? null,
    slaWarningAt: null,
    isOverdue: row.is_overdue ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    organizationId: row.organization_id,
    result: row.result ?? null,
    notes: row.notes ?? null,
    metadata,
    plannedDuration: row.planned_duration ?? null,
    plannedRoleId: row.planned_role_id ?? null,
    actualDuration: row.actual_duration ?? null,
    queueDuration: row.queue_duration ?? null,
    availableAt: row.available_at ?? null,
  };
}

// ============================================================================
// QUERIES
// ============================================================================

const TASK_SELECT = `
  *,
  stage:workflow_stages_v9(name, default_assignee_role),
  processInstance:workflow_instances_v9(
    title,
    reference_code,
    priority
  )
`;

function applyFilters(
  query: any,
  filters: TaskFilters,
) {
  if (filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  if (filters.overdue) {
    query = query.eq('is_overdue', true);
  }

  if (filters.assignee) {
    query = query.eq('assigned_to', filters.assignee);
  }

  if (filters.stageName) {
    query = query.eq('stage_id', filters.stageName);
  }

  if (filters.instanceId) {
    query = query.eq('instance_id', filters.instanceId);
  }

  if (filters.search && filters.search.trim().length > 0) {
    const term = `%${filters.search.trim()}%`;
    query = query.ilike('notes', term);
  }

  return query;
}

function applySorting(
  query: any,
  sortField: TaskSortField,
  sortDirection: SortDirection,
) {
  const ascending = sortDirection === 'asc';

  switch (sortField) {
    case 'sla_deadline':
      return query.order('sla_deadline', { ascending, nullsFirst: false });
    case 'created_at':
      return query.order('created_at', { ascending });
    case 'updated_at':
      return query.order('updated_at', { ascending });
    case 'stage_name':
      return query.order('stage_id', { ascending });
    case 'status':
      return query.order('status', { ascending });
    default:
      return query.order('created_at', { ascending: false });
  }
}

/**
 * Lista tasks por organizacao — usado pela Gestao (kanban tatico)
 */
export async function listTasksByOrganization(
  organizationId: string,
  filters: TaskFilters,
  sortField: TaskSortField = 'sla_deadline',
  sortDirection: SortDirection = 'asc',
  page: number = 1,
  pageSize: number = 50,
): Promise<TaskListResult> {
  // Count total
  let countQuery = supabase
    .from('workflow_tasks_v9')
    .select('id', { count: 'exact' })
    .eq('organization_id', organizationId)
    .limit(1);

  countQuery = applyFilters(countQuery, filters);
  const { count } = await countQuery;

  // Fetch page
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let dataQuery = supabase
    .from('workflow_tasks_v9')
    .select(TASK_SELECT)
    .eq('organization_id', organizationId)
    .range(from, to);

  dataQuery = applyFilters(dataQuery, filters);
  dataQuery = applySorting(dataQuery, sortField, sortDirection);

  const { data, error } = await dataQuery;
  if (error) throw error;

  return {
    items: (data ?? []).map(mapRowToTaskListItem),
    total: count ?? 0,
    page,
    pageSize,
  };
}

/**
 * Lista tasks atribuidas a um usuario — usado pela Minha Mesa
 */
export async function listTasksByAssignee(
  userId: string,
  organizationId: string,
  filters: TaskFilters,
  sortField: TaskSortField = 'sla_deadline',
  sortDirection: SortDirection = 'asc',
): Promise<TaskListItem[]> {
  let query = supabase
    .from('workflow_tasks_v9')
    .select(TASK_SELECT)
    .eq('organization_id', organizationId)
    .eq('assigned_to', userId);

  query = applyFilters(query, filters);
  query = applySorting(query, sortField, sortDirection);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map(mapRowToTaskListItem);
}

/** Mesmos filtros padrão da Fila de Demandas (Minha Mesa). */
export const ROLE_QUEUE_DEFAULT_FILTERS: TaskFilters = {
  status: ['pending', 'in_progress', 'paused'],
  priority: [],
  search: '',
  overdue: false,
};

function filterRoleQueueByAssignmentScope(
  items: TaskListItem[],
  rawRows: Record<string, unknown>[],
  assignments: UserAssignment[],
  departmentId?: string | null,
): TaskListItem[] {
  const scoped = departmentId
    ? assignments.filter(a => a.departmentId === departmentId)
    : assignments;
  if (scoped.length === 0) return [];

  const roleIds = new Set(scoped.map(a => a.roleId));
  const assignmentPairs = new Set(scoped.map(a => `${a.roleId}:${a.departmentId}`));
  const rowById = new Map(rawRows.map(r => [r.id as string, r]));

  return items.filter(task => {
    const row = rowById.get(task.id);
    const taskRoleId = (row?.assigned_role_id as string | null | undefined) ?? task.assignedRoleId ?? null;
    const taskDeptId = (row?.assigned_department_id as string | null | undefined) ?? null;

    if (!taskRoleId) {
      const taskRoleKey = task.assignedRoleKey ?? normalizeWorkflowRoleKey(task.assignedRoleName);
      return Boolean(taskRoleKey) && !departmentId;
    }

    if (!roleIds.has(taskRoleId)) return false;
    if (!taskDeptId) return true;
    if (departmentId && taskDeptId !== departmentId) return false;
    return assignmentPairs.has(`${taskRoleId}:${taskDeptId}`);
  });
}

export async function countRoleQueueTasks(
  userId: string,
  organizationId: string,
  departmentId?: string | null,
  filters: TaskFilters = ROLE_QUEUE_DEFAULT_FILTERS,
): Promise<number> {
  const tasks = await listTasksByRole(userId, organizationId, filters, 'sla_deadline', 'asc', departmentId);
  return tasks.length;
}

export async function listTasksByRole(
  userId: string,
  organizationId: string,
  filters: TaskFilters,
  sortField: TaskSortField = 'sla_deadline',
  sortDirection: SortDirection = 'asc',
  departmentId?: string | null,
): Promise<TaskListItem[]> {
  let roleIds = await listUserRoleIds(userId, organizationId);

  if (departmentId) {
    const assignments = await getUserAssignments(organizationId, userId);
    const deptRoleIds = [...new Set(
      assignments.filter(a => a.departmentId === departmentId).map(a => a.roleId),
    )];
    if (deptRoleIds.length === 0) return [];
    roleIds = roleIds.filter(id => deptRoleIds.includes(id));
  }

  if (roleIds.length === 0) {
    // Fallback: se não houver role IDs (dados antigos sem assigned_role_id),
    // tentar matching por roleKey via memberships
    const memberships = await listUserWorkflowRoles(userId, organizationId);
    const roleKeys = memberships
      .map((m) => m.roleKey)
      .filter((v): v is string => Boolean(v));
    if (roleKeys.length === 0) return [];

    let query = supabase
      .from('workflow_tasks_v9')
      .select(TASK_SELECT)
      .eq('organization_id', organizationId)
      .in('status', ['pending'])
      .is('assigned_to', null);

    query = applyFilters(query, filters);
    query = applySorting(query, sortField, sortDirection);

    const { data, error } = await query;
    if (error) throw error;

    const rawRows = data ?? [];
    const mapped = rawRows
      .map(mapRowToTaskListItem)
      .filter((task) => {
        const taskRoleKey = task.assignedRoleKey ?? normalizeWorkflowRoleKey(task.assignedRoleName);
        return Boolean(taskRoleKey && roleKeys.includes(taskRoleKey));
      });

    const assignments = await getUserAssignments(organizationId, userId);
    return filterRoleQueueByAssignmentScope(mapped, rawRows, assignments, departmentId);
  }

  // Caminho principal: filtrar por assigned_role_id no banco
  let query = supabase
    .from('workflow_tasks_v9')
    .select(TASK_SELECT)
    .eq('organization_id', organizationId)
    .in('status', ['pending'])
    .is('assigned_to', null)
    .in('assigned_role_id', roleIds);

  query = applyFilters(query, filters);
  query = applySorting(query, sortField, sortDirection);

  const { data, error } = await query;
  if (error) throw error;

  const rawRows = data ?? [];
  const mapped = rawRows.map(mapRowToTaskListItem);
  const assignments = await getUserAssignments(organizationId, userId);
  return filterRoleQueueByAssignmentScope(mapped, rawRows, assignments, departmentId);
}

/**
 * Lista tasks com filtro de escopo hierárquico (DATA SCOPE)
 * Substitui listTasksByOrganization para views com visibilidade automática.
 */
export async function listTasksWithScope(
  organizationId: string,
  scope: DataScope,
  filters: TaskFilters,
  sortField: TaskSortField = 'sla_deadline',
  sortDirection: SortDirection = 'asc',
  page: number = 1,
  pageSize: number = 50,
): Promise<TaskListResult> {
  // Count
  let countQuery = supabase
    .from('workflow_tasks_v9')
    .select('id', { count: 'exact' })
    .eq('organization_id', organizationId)
    .limit(1);
  countQuery = applyScopeFilter(countQuery, scope, 'assigned_to', 'department_id');
  countQuery = applyFilters(countQuery, filters);
  const { count } = await countQuery;

  // Fetch page
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let dataQuery = supabase
    .from('workflow_tasks_v9')
    .select(TASK_SELECT)
    .eq('organization_id', organizationId)
    .range(from, to);
  dataQuery = applyScopeFilter(dataQuery, scope, 'assigned_to', 'department_id');
  dataQuery = applyFilters(dataQuery, filters);
  dataQuery = applySorting(dataQuery, sortField, sortDirection);

  const { data, error } = await dataQuery;
  if (error) throw error;

  return {
    items: (data ?? []).map(mapRowToTaskListItem),
    total: count ?? 0,
    page,
    pageSize,
  };
}

/**
 * Lista tasks por papel COM escopo hierárquico
 */
export async function listTasksByRoleWithScope(
  userId: string,
  organizationId: string,
  scope: DataScope,
  filters: TaskFilters,
  sortField: TaskSortField = 'sla_deadline',
  sortDirection: SortDirection = 'asc',
): Promise<TaskListItem[]> {
  const roleIds = await listUserRoleIds(userId, organizationId);
  if (roleIds.length === 0) return [];

  let query = supabase
    .from('workflow_tasks_v9')
    .select(TASK_SELECT)
    .eq('organization_id', organizationId)
    .in('status', ['pending'])
    .is('assigned_to', null)
    .in('assigned_role_id', roleIds);

  query = applyScopeFilter(query, scope, 'assigned_to', 'department_id');
  query = applyFilters(query, filters);
  query = applySorting(query, sortField, sortDirection);

  const { data, error } = await query;
  if (error) throw error;

  return (data ?? []).map(mapRowToTaskListItem);
}

/**
 * Busca detalhe de uma task — com metadata parsed
 */
export async function getTaskDetail(taskId: string): Promise<TaskDetailItem> {
  const { data, error } = await supabase
    .from('workflow_tasks_v9')
    .select(TASK_SELECT)
    .eq('id', taskId)
    .single();

  if (error) throw error;
  if (!data) throw new Error('Task nao encontrada');

  const listItem = mapRowToTaskListItem(data);
  return parseTaskDetail(listItem);
}

/**
 * Busca logs de uma task
 */
export async function getTaskLogs(taskId: string) {
  const { data, error } = await supabase
    .from('workflow_task_logs')
    .select('id, task_id, action, performed_by, performed_by_name, performed_by_role, justification, metadata, created_at, timestamp')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });

  if (error) {
    // Fallback: if performed_by_name column doesn't exist, retry without it
    if (error.message?.includes('performed_by_name') || error.code === '42703') {
      const { data: fallback, error: fallbackErr } = await supabase
        .from('workflow_task_logs')
        .select('id, task_id, action, performed_by, performed_by_role, justification, metadata, created_at, timestamp')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });
      if (fallbackErr) throw fallbackErr;
      return fallback ?? [];
    }
    throw error;
  }
  return data ?? [];
}

/**
 * Busca resumo da etapa anterior (completed task da mesma instância com stage_order inferior)
 */
export async function getPreviousStageSummary(
  instanceId: string,
  currentStageOrder: number | null,
): Promise<{
  stageName: string;
  assignedUserName: string | null;
  completedAt: string | null;
  durationMinutes: number | null;
} | null> {
  if (!instanceId || currentStageOrder == null || currentStageOrder <= 1) return null;
  const { data, error } = await supabase
    .from('workflow_tasks_v9')
    .select('id, stage_id, assigned_user_name, assigned_to_name, completed_at, started_at, stage:workflow_stages_v9(name, stage_order)')
    .eq('instance_id', instanceId)
    .eq('status', 'completed')
    .order('completed_at', { ascending: false })
    .limit(10);
  if (error || !data || data.length === 0) return null;
  // Find the task from the stage immediately before current
  const prev = data.find((t: any) => {
    const order = (t as any).stage?.stage_order;
    return order != null && order < currentStageOrder;
  });
  if (!prev) return null;
  const stageName = (prev as any).stage?.name ?? 'Etapa anterior';
  const assignedUserName = (prev as any).assigned_user_name ?? (prev as any).assigned_to_name ?? null;
  const completedAt = (prev as any).completed_at ?? null;
  let durationMinutes: number | null = null;
  if ((prev as any).started_at && completedAt) {
    durationMinutes = Math.round((new Date(completedAt).getTime() - new Date((prev as any).started_at).getTime()) / 60_000);
  }
  return { stageName, assignedUserName, completedAt, durationMinutes };
}

// ============================================================================
// MUTATIONS
// ============================================================================

async function getTaskAssignmentContext(taskId: string) {
  const { data, error } = await supabase
    .from('workflow_tasks_v9')
    .select(`
      id,
      status,
      organization_id,
      instance_id,
      stage_id,
      created_at,
      assigned_to,
      assigned_at,
      assigned_role_id,
      assigned_user_id,
      claimed_at,
      started_at,
      completed_at,
      metadata,
      stage:workflow_stages_v9(default_assignee_role)
    `)
    .eq('id', taskId)
    .single();

  if (error) throw error;
  return data as Record<string, any>;
}

function getAssignedActorId(task: Record<string, any> | null | undefined): string | null {
  return (task?.assigned_to as string | null | undefined) ?? null;
}

function diffMinutes(start?: string | null, end?: string | null): number | null {
  if (!start || !end) return null;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(diff) || diff < 0) return null;
  return Math.round((diff / (1000 * 60)) * 100) / 100;
}

function resolveOperationalEnteredAt(task: Record<string, any>, metadata: TaskMetadata): string | null {
  const metrics = (metadata.operationalMetrics ?? {}) as TaskOperationalMetrics;
  return (
    metrics.enteredAt ??
    (typeof metadata.entered_at === 'string' ? metadata.entered_at : null) ??
    task.started_at ??
    task.assigned_at ??
    task.created_at ??
    null
  );
}

function buildOperationalMetrics(
  task: Record<string, any>,
  metadata: TaskMetadata,
  now: string,
): TaskOperationalMetrics {
  const previous = (metadata.operationalMetrics ?? {}) as TaskOperationalMetrics;
  const enteredAt = resolveOperationalEnteredAt(task, metadata);
  const claimedAt = task.assigned_at ?? previous.claimedAt ?? null;
  const startedAt = task.started_at ?? previous.startedAt ?? null;
  const completedAt = task.completed_at ?? previous.completedAt ?? null;
  const tpRealMinutes = diffMinutes(enteredAt, completedAt ?? now);
  const traRealMinutes = diffMinutes(startedAt, completedAt ?? now);
  const queueMinutes = tpRealMinutes !== null
    ? Math.max(0, tpRealMinutes - (traRealMinutes ?? 0))
    : null;
  const waitingBeforeClaimMinutes = diffMinutes(enteredAt, claimedAt);

  return {
    ...previous,
    enteredAt,
    claimedAt,
    startedAt,
    completedAt,
    tpRealMinutes,
    traRealMinutes,
    queueMinutes,
    waitingBeforeClaimMinutes,
    updatedAt: now,
  };
}

function appendOperationalHistory(
  metadata: TaskMetadata,
  entry: TaskOperationalHistoryEntry,
): TaskOperationalHistoryEntry[] {
  const currentHistory = Array.isArray(metadata.operationalHistory)
    ? metadata.operationalHistory
    : [];

  return [...currentHistory, entry];
}

function mergeOperationalMetadata(
  metadata: TaskMetadata,
  task: Record<string, any>,
  now: string,
  event: TaskOperationalHistoryEntry['event'],
  result?: string | null,
): TaskMetadata {
  const operationalMetrics = buildOperationalMetrics(task, metadata, now);

  return {
    ...metadata,
    operationalMetrics,
    operationalHistory: appendOperationalHistory(metadata, {
      ...operationalMetrics,
      event,
      capturedAt: now,
      result: (result ?? null) as TaskResult | null,
    }),
  };
}

async function assertUserCanOperateTask(task: Record<string, any>, userId: string): Promise<void> {
  // Resolve org role once — admins/managers bypass all checks
  const orgRole = await getOrgRole(userId, task.organization_id as string);
  if (orgRole === 'admin' || orgRole === 'manager') {
    return;
  }

  const assignedActorId = getAssignedActorId(task);
  if (assignedActorId && assignedActorId !== userId) {
    throw new Error('Esta tarefa foi assumida por outro usuario.');
  }

  // Verificar por assigned_role_id (UUID) — caminho principal
  const assignedRoleId = task.assigned_role_id as string | null | undefined;
  const assignedDeptId = task.assigned_department_id as string | null | undefined;

  if (assignedRoleId || assignedDeptId) {
    // Use exact (role+dept) pair match via user_assignments
    const assignments = await getUserAssignments(task.organization_id as string, userId);

    if (assignedRoleId && assignedDeptId) {
      // Both defined: must have exact pair
      const hasPair = assignments.some(a => a.roleId === assignedRoleId && a.departmentId === assignedDeptId);
      if (!hasPair) {
        throw new Error('Usuário não possui vínculo (cargo + departamento) para esta tarefa.');
      }
    } else if (assignedRoleId) {
      // Only role defined
      const hasRole = assignments.some(a => a.roleId === assignedRoleId);
      if (!hasRole) {
        // Fallback to legacy role check
        const allowed = await userHasWorkflowRole(userId, task.organization_id as string, assignedRoleId, null);
        if (!allowed) throw new Error('Usuário não pertence ao cargo desta tarefa.');
      }
    } else if (assignedDeptId) {
      // Only dept defined
      const hasDept = assignments.some(a => a.departmentId === assignedDeptId);
      if (!hasDept) {
        throw new Error('Usuário não pertence ao departamento desta tarefa.');
      }
    }
    return;
  }

  // Fallback: verificar por texto de role (metadata ou stage)
  const metadata = (task.metadata ?? {}) as TaskMetadata;
  const roleLike =
    (typeof metadata.role === 'string' ? metadata.role : null)
    ?? (task.stage?.default_assignee_role as string | null | undefined)
    ?? null;

  if (roleLike) {
    const allowed = await userHasWorkflowRole(userId, task.organization_id as string, null, roleLike);
    if (!allowed) {
      throw new Error('Usuario nao pertence ao papel estrutural desta tarefa.');
    }
  }
}

/**
 * Get the user's organization-level role (admin, manager, operator, etc).
 */
async function getOrgRole(userId: string, organizationId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('user_organizations')
    .select('role')
    .eq('user_id', userId)
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (error) {
    console.warn('[taskService] getOrgRole failed:', error.message);
  }
  return data?.role ?? null;
}

export async function claimTask(payload: TaskClaimPayload): Promise<void> {
  const { taskId, userId, userName } = payload;
  const now = new Date().toISOString();
  const task = await getTaskAssignmentContext(taskId);

  await assertUserCanOperateTask(task, userId);

  if (getAssignedActorId(task)) {
    if (getAssignedActorId(task) === userId) return;
    throw new Error('A tarefa ja foi assumida por outro usuario.');
  }

  const currentMeta = (task.metadata ?? {}) as TaskMetadata;
  const updatedMeta = mergeOperationalMetadata({
    ...currentMeta,
    operationalMetrics: {
      ...(currentMeta.operationalMetrics ?? {}),
      enteredAt: resolveOperationalEnteredAt(task, currentMeta) ?? now,
      claimedAt: task.assigned_at ?? now,
    },
  }, {
    ...task,
    assigned_at: task.assigned_at ?? now,
  }, now, 'claimed');

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      assigned_to: userId,
      assigned_user_id: userId,
      assigned_to_name: userName ?? null,
      assigned_at: task.assigned_at ?? now,
      claimed_at: now,
      metadata: updatedMeta,
      updated_at: now,
    })
    .eq('id', taskId)
    .is('assigned_to', null);

  if (error) throw error;

  await logTaskAction(taskId, 'claim', userId, 'Tarefa assumida pela fila do papel', {
    operationalMetrics: updatedMeta.operationalMetrics,
  });
}

/**
 * Inicia uma task (status pending → in_progress)
 */
export async function startTask(taskId: string, userId: string): Promise<void> {
  const now = new Date().toISOString();
  let task = await getTaskAssignmentContext(taskId);

  await assertUserCanOperateTask(task, userId);

  if (!getAssignedActorId(task)) {
    await claimTask({ taskId, userId });
    task = await getTaskAssignmentContext(taskId);
  }

  const currentMeta = (task.metadata ?? {}) as TaskMetadata;
  const updatedMeta = mergeOperationalMetadata({
    ...currentMeta,
    operationalMetrics: {
      ...(currentMeta.operationalMetrics ?? {}),
      enteredAt: resolveOperationalEnteredAt(task, currentMeta) ?? now,
      claimedAt: task.assigned_at ?? now,
      startedAt: now,
    },
  }, {
    ...task,
    assigned_at: task.assigned_at ?? now,
    started_at: now,
  }, now, 'started');

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      status: 'in_progress',
      assigned_to: userId,
      assigned_user_id: userId,
      assigned_at: task.assigned_at ?? now,
      claimed_at: task.claimed_at ?? task.assigned_at ?? now,
      started_at: now,
      // Registrar available_at se ainda não definido (momento em que ficou disponível para execução)
      available_at: (task as Record<string, unknown>).available_at ?? task.created_at ?? now,
      metadata: updatedMeta,
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'status_change', userId, 'Tarefa iniciada', {
    from: 'pending',
    to: 'in_progress',
    operationalMetrics: updatedMeta.operationalMetrics,
  });

  // Process Mining — fire-and-forget
  void logTaskTransition({
    organizationId: task.organization_id as string,
    taskId,
    source: 'workflow',
    userId,
    statusFrom: 'pending',
    statusTo: 'in_progress',
    previousTransitionAt: task.created_at as string | null,
  });
}

/**
 * Pausa uma task (status in_progress → paused)
 * TRA pauses (logged via history event), TP continues
 */
export async function pauseTask(taskId: string, userId: string, reason?: string): Promise<void> {
  const now = new Date().toISOString();
  const task = await getTaskAssignmentContext(taskId);

  await assertUserCanOperateTask(task, userId);

  if (task.status !== 'in_progress') {
    throw new Error('Somente tarefas em andamento podem ser pausadas.');
  }

  const currentMeta = (task.metadata ?? {}) as TaskMetadata;
  const updatedMeta = mergeOperationalMetadata(currentMeta, task, now, 'paused');
  
  if (reason) {
    updatedMeta.pauseReasons = updatedMeta.pauseReasons || [];
    updatedMeta.pauseReasons.push(reason);
  }

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      status: 'paused',
      metadata: updatedMeta,
      pause_reason: reason || null,
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'status_change', userId, 'Tarefa pausada', {
    from: 'in_progress',
    to: 'paused',
    reason,
    operationalMetrics: updatedMeta.operationalMetrics,
  });

  // Process Mining — fire-and-forget
  void logTaskTransition({
    organizationId: task.organization_id as string,
    taskId,
    source: 'workflow',
    userId,
    statusFrom: 'in_progress',
    statusTo: 'paused',
    previousTransitionAt: task.started_at as string | null,
  });
}

/**
 * Retoma uma task pausada (status paused → in_progress)
 */
export async function resumeTask(taskId: string, userId: string): Promise<void> {
  const now = new Date().toISOString();
  const task = await getTaskAssignmentContext(taskId);

  await assertUserCanOperateTask(task, userId);

  if (task.status !== 'paused') {
    throw new Error('Somente tarefas pausadas podem ser retomadas.');
  }

  const currentMeta = (task.metadata ?? {}) as TaskMetadata;
  const updatedMeta = mergeOperationalMetadata(currentMeta, task, now, 'resumed');

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      status: 'in_progress',
      metadata: updatedMeta,
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'status_change', userId, 'Tarefa retomada', {
    from: 'paused',
    to: 'in_progress',
    operationalMetrics: updatedMeta.operationalMetrics,
  });

  // Process Mining — fire-and-forget
  void logTaskTransition({
    organizationId: task.organization_id as string,
    taskId,
    source: 'workflow',
    userId,
    statusFrom: 'paused',
    statusTo: 'in_progress',
  });
}

/**
 * Completa uma task
 */
export async function completeTask(payload: TaskCompletionPayload): Promise<void> {
  const { taskId, result, notes, formData, checklistData, completedBy, previousUpdatedAt } = payload;
  const now = new Date().toISOString();


  // Buscar task atual para metadata merge
  const { data: current, error: fetchError } = await supabase
    .from('workflow_tasks_v9')
    .select('metadata, instance_id, stage_id, updated_at, organization_id, assigned_to, assigned_at, created_at, started_at, completed_at, stage:workflow_stages_v9(default_assignee_role)')
    .eq('id', taskId)
    .single();

  if (fetchError) throw fetchError;
  await assertUserCanOperateTask(current as Record<string, any>, completedBy);

  // Optimistic lock: check updated_at
  if (current?.updated_at !== previousUpdatedAt) {
    const err: any = new Error('Task was updated by another user.');
    err.code = 'OPTIMISTIC_LOCK_CONFLICT';
    err.details = {
      taskId,
      currentUpdatedAt: current?.updated_at,
      previousUpdatedAt,
    };
    throw err;
  }

  const currentMeta = (current?.metadata ?? {}) as TaskMetadata;
  const updatedMeta: TaskMetadata = mergeOperationalMetadata({
    ...currentMeta,
    ...(formData ? { formData } : {}),
    ...(checklistData ? { checklistData } : {}),
  }, {
    ...current,
    completed_at: now,
  }, now, 'completed', result);
  const durationMinutes = updatedMeta.operationalMetrics?.traRealMinutes ?? diffMinutes(current?.started_at, now);

  // Calcular actual_duration e queue_duration para colunas dedicadas (Yamazumi Real)
  const actualDurationMinutes = current?.started_at
    ? Math.round(diffMinutes(current.started_at, now))
    : null;
  const availableAtTs = (current as Record<string, unknown>)?.available_at as string | null | undefined;
  const queueDurationMinutes = availableAtTs && current?.started_at
    ? Math.round(Math.max(0, diffMinutes(availableAtTs, current.started_at)))
    : null;

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      status: 'completed',
      result,
      notes: notes ?? null,
      assigned_to: completedBy,
      completed_at: now,
      updated_at: now,
      metadata: updatedMeta,
      actual_duration: actualDurationMinutes,
      queue_duration: queueDurationMinutes,
    })
    .eq('id', taskId)
    .eq('updated_at', previousUpdatedAt); // Optimistic lock

  if (error) {
    if (error.code === 'PGRST116' || error.message?.includes('No rows')) {
      const err: any = new Error('Task was updated by another user.');
      err.code = 'OPTIMISTIC_LOCK_CONFLICT';
      err.details = {
        taskId,
        previousUpdatedAt,
      };
      structuredLogger.error('Conflito de concorrência ao concluir task', {
        operation: 'completeTask',
        taskId,
        organizationId: current?.metadata?.organization_id as string | undefined,
        userId: completedBy,
        extra: { previousUpdatedAt },
      }, err);
      throw err;
    }
    structuredLogger.error('Erro ao concluir task', {
      operation: 'completeTask',
      taskId,
      userId: completedBy,
    }, error);
    throw error;
  }

  await logTaskAction(taskId, 'status_change', completedBy, notes ?? 'Tarefa concluida', {
    from: 'in_progress',
    to: 'completed',
    result,
    operationalMetrics: updatedMeta.operationalMetrics,
  });

  // Auto-detecção de desvio: se duração real > 25% acima do planejado, registrar problema automaticamente
  const plannedMin = (current as Record<string, unknown>)?.planned_duration as number | null | undefined;
  if (actualDurationMinutes && plannedMin && plannedMin > 0) {
    const deviation = actualDurationMinutes / plannedMin;
    if (deviation > 1.25) {
      try {
        await registerProblem({
          taskId,
          description: `Desvio de execução detectado automaticamente: planejado ${Math.round(plannedMin)}min, real ${Math.round(actualDurationMinutes)}min (+${Math.round((deviation - 1) * 100)}%).`,
          problemType: 'performance',
          impact: deviation > 1.5 ? 'high' : 'medium',
          performedBy: completedBy,
        });
      } catch {
        // Falha silenciosa — não impede conclusão da task
      }
    }
  }

  // Process Mining — fire-and-forget
  void logTaskTransition({
    organizationId: current?.organization_id as string,
    taskId,
    source: 'workflow',
    userId: completedBy,
    statusFrom: 'in_progress',
    statusTo: 'completed',
    previousTransitionAt: current?.started_at as string | null,
    metadata: { result, durationMinutes },
  });

  // Avancar instancia para proxima etapa
  if (current?.instance_id && current?.stage_id) {
    await advanceInstance(current.instance_id, current.stage_id);
  }
}

/**
 * Rejeita/devolve uma task (approve_reject)
 */
export async function rejectTask(
  taskId: string,
  userId: string,
  reason: string,
): Promise<void> {
  const now = new Date().toISOString();
  const task = await getTaskAssignmentContext(taskId);

  await assertUserCanOperateTask(task, userId);

  const currentMeta = (task.metadata ?? {}) as TaskMetadata;
  const updatedMeta = mergeOperationalMetadata(currentMeta, {
    ...task,
    completed_at: now,
  }, now, 'failed', 'rejected');
  const durationMinutes = updatedMeta.operationalMetrics?.traRealMinutes ?? diffMinutes(task.started_at, now);

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      status: 'failed',
      result: 'rejected',
      notes: reason,
      assigned_to: userId,
      completed_at: now,
      metadata: updatedMeta,
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'status_change', userId, reason, {
    from: 'in_progress',
    to: 'failed',
    result: 'rejected',
    operationalMetrics: updatedMeta.operationalMetrics,
  });
}

/**
 * Reassigna uma task — acao de Gestao
 */
export async function reassignTask(payload: TaskReassignPayload): Promise<void> {
  const { taskId, newAssignee, justification, performedBy } = payload;
  const now = new Date().toISOString();

  const { data: current } = await supabase
    .from('workflow_tasks_v9')
    .select('assigned_to')
    .eq('id', taskId)
    .single();

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      assigned_to: newAssignee,
      assigned_at: now,
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'reassign', performedBy, justification, {
    from: current?.assigned_to,
    to: newAssignee,
  });

  // Fire-and-forget: notificar novo atribuído
  notifyTaskAssigned({ userId: newAssignee, taskTitle: '', taskId });
}

/**
 * Transfere uma task para outro usuario (operador → operador)
 */
export async function transferTask(payload: {
  taskId: string;
  newAssigneeId: string;
  newAssigneeName?: string;
  justification: string;
  performedBy: string;
}): Promise<void> {
  const { taskId, newAssigneeId, newAssigneeName, justification, performedBy } = payload;
  const now = new Date().toISOString();

  const task = await getTaskAssignmentContext(taskId);
  await assertUserCanOperateTask(task, performedBy);

  const previousAssignee = getAssignedActorId(task);

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      assigned_to: newAssigneeId,
      assigned_user_id: newAssigneeId,
      assigned_to_name: newAssigneeName ?? null,
      assigned_at: now,
      status: 'pending',
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'reassign', performedBy, justification, {
    from: previousAssignee,
    to: newAssigneeId,
    toName: newAssigneeName,
    type: 'transfer',
  });

  // Fire-and-forget: notificar transferência
  notifyTaskTransferred({ userId: newAssigneeId, stageName: newAssigneeName ?? 'Transferência', taskId });
}

/**
 * Escala uma task — registra evento e marca flag
 */
export async function escalateTask(payload: {
  taskId: string;
  justification: string;
  performedBy: string;
}): Promise<void> {
  const { taskId, justification, performedBy } = payload;
  const now = new Date().toISOString();

  const task = await getTaskAssignmentContext(taskId);
  const currentMeta = (task.metadata ?? {}) as TaskMetadata;

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      metadata: { ...currentMeta, escalated: true, escalatedAt: now, escalatedBy: performedBy },
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'escalate', performedBy, justification, {
    type: 'escalation',
  });

  // Fire-and-forget: notificar escalonamento ao admin da org
  supabase
    .from('workflow_tasks_v9')
    .select('organization_id')
    .eq('id', taskId)
    .single()
    .then(({ data: t }) => {
      if (!t?.organization_id) return;
      supabase
        .from('user_organizations')
        .select('user_id')
        .eq('organization_id', t.organization_id)
        .eq('role', 'admin')
        .limit(1)
        .single()
        .then(({ data: adminRow }) => {
          if (adminRow) {
            notifyEscalation({ userId: adminRow.user_id, stageName: 'Escalonamento', justification, taskId });
          }
        });
    });
}

/**
 * Devolve uma task para fila de papel (remove atribuição, volta a pending)
 */
export async function returnTask(payload: {
  taskId: string;
  justification: string;
  performedBy: string;
}): Promise<void> {
  const { taskId, justification, performedBy } = payload;
  const now = new Date().toISOString();

  const task = await getTaskAssignmentContext(taskId);
  await assertUserCanOperateTask(task, performedBy);

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      assigned_to: null,
      assigned_user_id: null,
      assigned_to_name: null,
      assigned_at: null,
      claimed_at: null,
      status: 'pending',
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'return', performedBy, justification, {
    type: 'return_to_queue',
    previousAssignee: performedBy,
  });

  // Fire-and-forget: notificar membros do papel que a tarefa voltou à fila
  // (notificação vai para o papel via role queue — por ora notifica o performer como ack)
}

/**
 * Registra observação como evento no log (sem mudar status)
 */
export async function addTaskNote(payload: {
  taskId: string;
  note: string;
  performedBy: string;
  attachments?: Array<{ name: string; url: string; size?: string }>;
}): Promise<void> {
  const { taskId, note, performedBy, attachments } = payload;

  const details: Record<string, any> = { type: 'observation' };
  if (attachments && attachments.length > 0) {
    details.attachments = attachments;
  }

  await logTaskAction(taskId, 'note', performedBy, note, details);
}

/**
 * Registra um problema/ocorrência na task (não muda status)
 */
export async function registerProblem(payload: {
  taskId: string;
  description: string;
  problemType: string;
  impact: string;
  performedBy: string;
}): Promise<void> {
  const { taskId, description, problemType, impact, performedBy } = payload;
  const now = new Date().toISOString();

  const task = await getTaskAssignmentContext(taskId);
  const currentMeta = (task.metadata ?? {}) as TaskMetadata;
  const problems = (currentMeta.problems as Array<Record<string, unknown>>) ?? [];

  problems.push({
    id: crypto.randomUUID(),
    description,
    type: problemType,
    impact,
    reportedBy: performedBy,
    reportedAt: now,
  });

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      metadata: { ...currentMeta, problems },
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'register_problem', performedBy, description, {
    type: 'problem',
    problemType,
    impact,
  });
}

/**
 * Pula uma task — acao de Gestao
 */
export async function skipTask(payload: TaskSkipPayload): Promise<void> {
  const { taskId, justification, performedBy } = payload;
  const now = new Date().toISOString();

  const { data: current } = await supabase
    .from('workflow_tasks_v9')
    .select('instance_id, stage_id, metadata, assigned_at, created_at, started_at, completed_at')
    .eq('id', taskId)
    .single();

  const meta = (current?.metadata ?? {}) as TaskMetadata;
  if (!meta.rules?.canSkip) {
    throw new Error('Blueprint nao permite pular esta etapa');
  }

  const currentMeta = (current?.metadata ?? {}) as TaskMetadata;
  const updatedMeta = mergeOperationalMetadata(currentMeta, {
    ...current,
    completed_at: now,
  }, now, 'skipped', 'skipped');

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      status: 'skipped',
      completed_at: now,
      metadata: updatedMeta,
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'skip', performedBy, justification, {
    operationalMetrics: updatedMeta.operationalMetrics,
  });

  // Avancar instancia
  if (current?.instance_id && current?.stage_id) {
    await advanceInstance(current.instance_id, current.stage_id);
  }
}

/**
 * Ajusta SLA de uma task — acao de Gestao
 */
export async function adjustTaskSLA(payload: TaskSLAAdjustPayload): Promise<void> {
  const { taskId, newDeadline, justification, performedBy } = payload;
  const now = new Date().toISOString();

  const { data: current } = await supabase
    .from('workflow_tasks_v9')
    .select('sla_deadline')
    .eq('id', taskId)
    .single();

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      sla_deadline: newDeadline,
      is_overdue: new Date(newDeadline) < new Date(),
      updated_at: now,
    })
    .eq('id', taskId);

  if (error) throw error;

  await logTaskAction(taskId, 'adjust_sla', performedBy, justification, {
    old_deadline: current?.sla_deadline,
    new_deadline: newDeadline,
  });
}

/**
 * Salva form data parcial (sem completar)
 */
export async function saveFormData(
  taskId: string,
  formData: Record<string, unknown>,
  checklistData?: Record<string, boolean>,
): Promise<void> {
  const { data: current, error: fetchError } = await supabase
    .from('workflow_tasks_v9')
    .select('metadata')
    .eq('id', taskId)
    .single();

  if (fetchError) throw fetchError;

  const currentMeta = (current?.metadata ?? {}) as TaskMetadata;
  const updatedMeta: TaskMetadata = {
    ...currentMeta,
    formData,
    ...(checklistData ? { checklistData } : {}),
  };

  const { error } = await supabase
    .from('workflow_tasks_v9')
    .update({
      metadata: updatedMeta,
      updated_at: new Date().toISOString(),
    })
    .eq('id', taskId);

  if (error) throw error;
}

// ============================================================================
// AVANCAR INSTANCIA — apos completar/pular task
// ============================================================================

/**
 * Avanca a instancia para a proxima etapa apos conclusao de uma task
 */
export async function advanceInstance(
  instanceId: string,
  completedStageId: string,
): Promise<void> {
  // Buscar todas as tasks da instancia
  const { data: tasks, error: tasksError } = await supabase
    .from('workflow_tasks_v9')
    .select('id, stage_id, status, metadata')
    .eq('instance_id', instanceId)
    .order('created_at', { ascending: true });

  if (tasksError) throw tasksError;
  if (!tasks || tasks.length === 0) return;

  // Encontrar proxima task pendente
  const completedStageOrder = (tasks.find(t => t.stage_id === completedStageId)?.metadata as TaskMetadata)?.blueprint_stage_order ?? -1;
  const nextPending = tasks.find(t => {
    const order = (t.metadata as TaskMetadata)?.blueprint_stage_order ?? -1;
    return order > completedStageOrder && (t.status === 'pending' || t.status === 'waiting');
  });

  if (nextPending) {
    const now = new Date().toISOString();
    // Ativar proxima task (waiting → pending)
    if (nextPending.status === 'waiting') {
      const currentMeta = (nextPending.metadata ?? {}) as TaskMetadata;
      const updatedMeta = mergeOperationalMetadata({
        ...currentMeta,
        operationalMetrics: {
          ...(currentMeta.operationalMetrics ?? {}),
          enteredAt: now,
        },
      }, {
        ...nextPending,
        created_at: now,
        assigned_at: null,
        started_at: null,
        completed_at: null,
      }, now, 'activated');

      // Calcular SLA da proxima etapa
      const { data: nextStage } = await supabase
        .from('workflow_stages_v9')
        .select('sla_hours, warning_threshold_percent')
        .eq('id', nextPending.stage_id)
        .maybeSingle();

      const activatedAt = new Date();
      const slaDeadline = nextStage?.sla_hours
        ? new Date(activatedAt.getTime() + nextStage.sla_hours * 60 * 60 * 1000)
        : null;
      const warnPct = nextStage?.warning_threshold_percent ?? 80;
      const slaWarningAt = nextStage?.sla_hours
        ? new Date(activatedAt.getTime() + (nextStage.sla_hours * 60 * 60 * 1000 * warnPct) / 100)
        : null;

      await supabase
        .from('workflow_tasks_v9')
        .update({
          status: 'pending',
          assigned_to: null,
          metadata: updatedMeta,
          updated_at: now,
          ...(slaDeadline ? { sla_deadline: slaDeadline.toISOString() } : {}),
          ...(slaWarningAt ? { sla_warning_at: slaWarningAt.toISOString() } : {}),
        })
        .eq('id', nextPending.id);
    }

    // Atualizar current_stage_id da instancia
    await supabase
      .from('workflow_instances_v9')
      .update({
        current_stage_id: nextPending.stage_id,
        updated_at: now,
      })
      .eq('id', instanceId);
  } else {
    // Todas as tasks concluidas — completar instancia
    const allDone = tasks.every(t =>
      t.stage_id === completedStageId
        ? true // a que acabamos de completar
        : ['completed', 'skipped', 'cancelled'].includes(t.status)
    );

    if (allDone) {
      await supabase
        .from('workflow_instances_v9')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', instanceId);

      // Send workflow completion notification (non-blocking)
      supabase
        .from('workflow_instances_v9')
        .select('title, reference_code, started_at, organization_id')
        .eq('id', instanceId)
        .single()
        .then(({ data: inst }) => {
          if (!inst) return;
          supabase
            .from('user_organizations')
            .select('user_id')
            .eq('organization_id', inst.organization_id)
            .eq('role', 'admin')
            .limit(1)
            .maybeSingle()
            .then(({ data: adminRow }) => {
              if (!adminRow) return;
              supabase.from('user_profiles').select('email').eq('user_id', adminRow.user_id).maybeSingle()
                .then(({ data: profile }) => {
                  if (profile?.email) {
                    sendWorkflowCompletedEmail(profile.email, {
                      workflowName: inst.title || inst.reference_code || instanceId,
                      completedAt: new Date().toISOString(),
                      totalTasks: tasks.length,
                    }).catch(() => {});
                  }
                });
            });
        });
    }
  }
}

// ============================================================================
// LOG — registra acao no workflow_task_logs
// ============================================================================

async function logTaskAction(
  taskId: string,
  action: string,
  performedBy: string,
  justification: string,
  details: Record<string, any>,
): Promise<void> {
  try {
    // Fetch task context for required columns
    const { data: task } = await supabase
      .from('workflow_tasks_v9')
      .select('instance_id, stage_id, organization_id, metadata')
      .eq('id', taskId)
      .maybeSingle();

    if (!task) return;

    const metadata = (task.metadata ?? {}) as TaskMetadata;
    const performedByRole =
      (typeof metadata.role === 'string' ? metadata.role : null)
      ?? metadata.roleName
      ?? 'operador';

    // Resolve performer name
    let performedByName: string | null = null;
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('name')
        .eq('user_id', performedBy)
        .maybeSingle();
      performedByName = profile?.name ?? null;
    } catch { /* profile lookup is best-effort */ }

    const logRow: Record<string, any> = {
      task_id: taskId,
      organization_id: task.organization_id,
      process_instance_id: task.instance_id,
      stage_id: task.stage_id,
      action,
      performed_by: performedBy,
      performed_by_role: performedByRole,
      justification: justification.length >= 10 ? justification : justification.padEnd(10, '.'),
      metadata: details,
    };
    if (performedByName) logRow.performed_by_name = performedByName;

    const { error: insertErr } = await supabase.from('workflow_task_logs').insert(logRow);
    // Retry without performed_by_name if column doesn't exist
    if (insertErr && (insertErr.message?.includes('performed_by_name') || insertErr.code === '42703')) {
      delete logRow.performed_by_name;
      await supabase.from('workflow_task_logs').insert(logRow);
    }
  } catch (err) {
    console.warn('[taskService] Falha ao registrar log:', err);
  }
}
